import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../Styles/DatePicker.scss";

/**
 * CustomDatePicker Component
 * 
 * A wrapper around react-datepicker with custom styling and configuration.
 * Supports both date and time selection.
 * 
 * @component
 * @param {Object} props
 * @param {Date} props.selected - The currently selected date
 * @param {Function} props.onChange - Callback function when date changes
 * @param {Date} props.minDate - Minimum selectable date (default: today)
 * @param {string} props.type - "date" or "time" (default: "date")
 * 
 * @example
 * <CustomDatePicker
 *   selected={startDate}
 *   onChange={(date) => setStartDate(date)}
 *   type="date"
 * />
 */
const CustomDatePicker = ({
  selected,
  onChange,
  minDate = new Date(), // Prevent dates before today
  maxDate,
  className = "",
  placeholderText = "Select date",
  showYearDropdown = true,
  scrollableYearDropdown = true,
  showMonthDropdown = true,
  popperClassName = "!z-[9999]",
  type = "date", // Accepts "date" or "time"
  timeIntervals = 15,
  ...props
}) => {
  const isTimeOnly = type === "time";

  return (
    <DatePicker
      selected={selected}
      onChange={onChange}
      minDate={minDate}
      maxDate={maxDate}
      className={`${className}`}
      dateFormat={isTimeOnly ? "h:mm aa" : "yyyy-MM-dd h:mm aa"}
      wrapperClassName="w-full"
      calendarClassName="!bg-white !border !border-gray-200 !rounded-xl !shadow-lg !p-4"
      dayClassName={() =>
        "hover:bg-red-50 rounded-full transition-colors duration-200"
      }
      popperClassName={`${popperClassName} !z-[10001]`}
      showYearDropdown={!isTimeOnly && showYearDropdown}
      scrollableYearDropdown={!isTimeOnly && scrollableYearDropdown}
      dropdownMode="select"
      showMonthDropdown={!isTimeOnly && showMonthDropdown}
      showTimeSelect={isTimeOnly}
      showTimeSelectOnly={isTimeOnly}
      timeIntervals={isTimeOnly ? timeIntervals : undefined}
      timeClassName={() =>
        "!bg-white !rounded-md hover:!bg-red-50 transition-colors duration-200"
      }
      popperModifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
      ]}
      portalId="root"
      placeholderText={placeholderText}
      {...props}
    />
  );
};

export default CustomDatePicker;
